import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, DatabaseRecordType, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../../framework/appUtils";
import * as store from "../../../../../framework/customStore";
import { SaveDataObj } from "../../../../../framework/saveData";
import * as calendarTaskService from "../../../../../models/services/calendarTaskService";
import * as storeActions from "../../../../../models/store/storeActions";
import { Dialog } from "../../../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../../framework/Grid/GridRow";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { ColorPicker } from "source/components/framework/ColorPicker/ColorPicker";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { ICalendarTaskCategoryEditItem } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryEditItem";
import { Base } from "ui/Scripts/source/framework/base";

export interface ICalendarTaskCategoryDialogProp {
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: () => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface ICalendarTaskCategoryDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    color: string;
    sortOrder: string;
    databaseRecordType: DatabaseRecordType;
    activeState: number;
    item: ICalendarTaskCategoryEditItem;
}

export class CalendarTaskCategoryDialog extends React.Component<ICalendarTaskCategoryDialogProp, ICalendarTaskCategoryDialogState> {
    private orgStateHash: string = "";

    constructor(props: ICalendarTaskCategoryDialogProp) {
        super(props);
        this.state = {
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            color: "",
            sortOrder: "0",
            databaseRecordType: null,
            activeState: 0,
            item: null,
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskCategoryEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.item;
        const state = {
            ...this.state,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            color: item.color,
            sortOrder: item.sortOrder.toString(10),
            databaseRecordType: item.databaseRecordType,
            activeState: item.activeState,
            item
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: ICalendarTaskCategoryDialogProp, prevState: ICalendarTaskCategoryDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(this.props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "sortOrder") {
            this.setState({ sortOrder: value });
        }
    };

    getSaveDataFromState = (props: ICalendarTaskCategoryDialogProp, state: ICalendarTaskCategoryDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("color", state.color);
        result.append("sortOrder", state.sortOrder.toInteger());
        result.append("databaseRecordType", state.databaseRecordType);
        result.append("activeState", state.activeState);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return false;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => calendarTaskService.saveCalendarTaskCategoryEdit(saveData.obj));
        if (!result) return false;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return true;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(this.state.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose();
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCalendarTaskCategoryParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose();
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            this.saveToDb,
            props.onRefreshList,
            (newStateHash: string) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.id) return null;
        const isNew = state.isNew;
        const databaseRecordTypeSpecified = !Base.isNullOrUndefined(state.databaseRecordType);
        return (
            <Dialog
                id="taskStateDialog"
                maxWidth="sm"
                title={isNew ? Translations.AddCalendarTaskCategory : (Translations.CalendarTaskCategory + ": " + state.item.code)}
                body={
                    <Box display="flex" flexDirection="column">
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} fullWidth required margin="0">
                                    <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description} fullWidth>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={4}>
                                <FormField title={Translations.Color} fullWidth overflow="visible">
                                    <ColorPicker
                                        requiredColors={[state.color]}
                                        value={state.color}
                                        onChange={color => this.setState({ color })}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={4}>
                                <FormField title={Translations.SortOrder} fullWidth>
                                    <TextField name="sortOrder" title={Translations.SortOrder} type="number" inputProps={{ maxLength: 5 }} value={state.sortOrder} onChange={this.handleChange} size="small" fullWidth InputProps={{ inputProps: { min: 1, max: 100 } }} />
                                </FormField>
                            </Grid>
                            {databaseRecordTypeSpecified &&
                                <Grid item sm={4}>
                                    <FormField title={Translations.RelatedToDatabaseRecordType} fullWidth>
                                        <TextField title={Translations.RelatedToDatabaseRecordType} value={EnumHelper.DatabaseRecordTypeToStr(state.databaseRecordType)} disabled size="small" fullWidth />
                                    </FormField>
                                </Grid>
                            }
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title="" fullWidth>
                                    <FormCheckBox
                                        title={Translations.InUse}
                                        checked={state.activeState > 0}
                                        disabled={databaseRecordTypeSpecified}
                                        onChange={(checked: boolean) => { this.setState({ activeState: checked ? 1 : 0 }); }}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onRemove={!isNew && Base.isNullOrUndefined(state.databaseRecordType) ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}